import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* Helpers */
import { utils } from "../../helpers"

/* Toast */
import { pushToast } from "../../components/Toast"
import { pushConfirm } from "../../components/Confirm"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"
import { CloseIcon, RefundIcon, PayIcon } from "../../components/Icons"


/*
    Widget RoundPayment — THE REFUND AND THE PAYMENT OF ONE PLAYER

    Opened by a ref: open({ type, game }), where type is "refund" or "payment" and game is the row
    of the player in the round. Nothing leaves the modal until the amount is confirmed.

    api      the module of the game — credit
    build    how the game names the fields of its credit call, the multi player shape by default
    apply    the card behind the modal reads itself again
*/
class RoundPayment extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            type: "",
            amount: "",
            waiting: false,
            game: null,
        }
    }

    /* Open Action */
    open = ({ type, game }) => {

        /* Check Parameters */
        if (!type || !game) {
            return
        }

        this.setState({ visible: true, type, amount: "", game })
    }

    /* Close Action — never while the operator wallet is being waited for */
    close = () => {

        /* Check Waiting */
        if (this.state.waiting) {
            return
        }

        this.setState({ visible: false, type: "", amount: "" })
    }

    /* Send Action */
    send = () => {

        /* Fields */
        const { api, uid, postfix, socket, apply, build } = this.props
        const { amount, type, game } = this.state

        /* Check Amount */
        if (`${amount}`.trim() === "" || !(parseFloat(amount) > 0)) {
            pushToast({ text: t("widget.amountError"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: type === "refund" ? t("widget.refundTitle") : t("widget.payTitle"),
            text: `${utils.convertor(amount, game.symbol, game.isAfter)} — ${game.player}`,
            icon: "warning",
            confirmText: t("widget.pay"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            this.setState({ waiting: true })

            /* The Body Of The Call — every live game but the vip ones names the round by its gameID */
            const body = build
                ? build({ uid, type, amount, game, postfix })
                : { uid, type, refund: amount, playerID: game.player, gameID: game.gameID, postfix }

            /* Send to REST API */
            api.credit(body).then((response) => {
                if (response.status === 200) {

                    /* Notify The Player */
                    if (socket) {
                        socket.emit("adminNotification", {
                            type: type === "refund" ? "refund" : "payment",
                            title: type === "refund" ? t("widget.refundTitle2") : t("widget.payTitle2"),
                            total: amount,
                            currency: game.currency,
                            playerID: game.player,
                        })
                    }

                    pushToast({ text: type === "refund" ? t("widget.refundSuccess") : t("widget.paySuccess"), type: "success" })
                    this.setState({ visible: false, type: "", amount: "" })
                }
                else {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            }).finally(() => {

                this.setState({ waiting: false })

                /* Read The Card Again */
                if (apply) {
                    apply()
                }
            })

        })

    }

    render = () => {

        /* Fields */
        const { visible, type, amount, waiting, game } = this.state

        /* Check Game */
        if (!game) {
            return null
        }

        /* Fields */
        const refund = type === "refund"

        /* Niu Niu has no ante: three independent stakes ride on a box and their face value
           comes back under "stake". Every other game keeps naming it an ante. */
        const staked = game.stake !== undefined && game.stake !== null ? game.stake : game.ante

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="rnd--modal small"
                overlayClassName="rnd--overlay"
                closeTimeoutMS={200}
            >

                <div className="rnd--modal-header">

                    <div className={`rnd--modal-icon ${refund ? "violet" : "pink"}`}>
                        {refund ? <RefundIcon size={18} /> : <PayIcon size={18} />}
                    </div>

                    <div className="rnd--modal-text">
                        <h2>{refund ? t("widget.refundTitle") : t("widget.payTitle")}</h2>
                        <p>{`${utils.checkValue(game.firstName)}`.trim() === "" ? game.player : `${game.firstName} · ${game.player}`}</p>
                    </div>

                    <div onClick={() => this.close()} className="rnd--modal-close"><CloseIcon size={16} /></div>

                </div>

                <div className="rnd--modal-body">

                    <div className="rnd--pay-facts">

                        {/* What the player put on the table. Most games open on an ante and
                            name it as one; a game whose round has no ante — Niu Niu, where
                            three independent stakes ride on a box — sends the face value of
                            all of them under "stake" instead, and is named for that. */}
                        <div className="rnd--pay-fact">
                            <span>{refund || staked !== game.ante ? t("widget.totalBet") : "ANTE"}</span>
                            <p>{utils.convertor(refund ? game.total : staked, game.symbol, game.isAfter)}</p>
                        </div>

                        <div className="rnd--pay-fact">
                            <span>{refund ? t("widget.refunded") : t("widget.paid")}</span>
                            <p>{utils.convertor(refund ? game.refund : game.paid, game.symbol, game.isAfter)}</p>
                        </div>

                    </div>

                    <div className="rnd--pay-field">
                        <span>{game.symbol ? game.symbol : game.currency}</span>
                        <input
                            placeholder={t("widget.enterAmount")}
                            type="text"
                            value={amount}
                            onChange={(event) => this.setState({ amount: event.target.value.replace(/[^\d.]/g, "") })}
                        />
                    </div>

                </div>

                <div className="rnd--modal-footer">

                    <div onClick={() => this.close()} className={`rnd--button ghost ${waiting ? "disabled" : ""}`}>{t("common.cancel")}</div>

                    {waiting
                        ? <div className="rnd--button primary disabled"><LoadingOutlined /></div>
                        : <div onClick={() => this.send()} className="rnd--button primary">{t("widget.pay")}</div>
                    }

                </div>

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(RoundPayment)
